import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { authClient, signInWithTelegram } from "@/lib/auth";
import { resolveAuthenticated } from "@/lib/devConfig";

/** The slice of the session user the profile surfaces actually render. */
export type ProfileUser = {
  id: string;
  name: string;
  image?: string | null;
};

/** IP/RP/admin subset of the home summary — all the top bar needs. */
export type ProfileSummary = {
  ip: number;
  rp: number;
  isAdmin: boolean;
};

/**
 * Module-level cache so the summary RPC fires at most once per signed-in user
 * regardless of how many profile surfaces mount (desktop chip, mobile drawer).
 * Fetch strategy: LAZY — nothing is requested on page load; `ensureSummary()`
 * runs on first dropdown open / drawer open and the result is reused after.
 */
let summaryCache: { key: string; value: ProfileSummary } | null = null;
let summaryInflight: { key: string; promise: Promise<ProfileSummary> } | null = null;
/** User key whose last fetch attempt failed — cleared on success or sign-out. */
let summaryErrorKey: string | null = null;
const summaryListeners = new Set<() => void>();

function resetSummaryCache() {
  summaryCache = null;
  summaryInflight = null;
  summaryErrorKey = null;
}

/**
 * Push a fresh balance into the cached summary from flows that receive one in
 * an RPC response (gacha pull, marketplace buy) so the top-bar chip doesn't go
 * stale. No-op while the cache is empty — the first dropdown open fetches a
 * post-mutation summary anyway.
 */
export function patchProfileSummary(patch: Partial<ProfileSummary>) {
  if (!summaryCache) return;
  summaryCache = { key: summaryCache.key, value: { ...summaryCache.value, ...patch } };
  for (const notify of summaryListeners) notify();
}

/**
 * Seed the cache with a FULL summary another flow already fetched (Draw's
 * fetchDrawSnapshot calls the same get_home_summary RPC) so the first dropdown
 * open / TopBar isAdmin read doesn't refire an identical request. Unlike
 * patchProfileSummary this works on an empty cache — it carries every field.
 */
export function seedProfileSummary(key: string, value: ProfileSummary) {
  summaryCache = { key, value };
  summaryErrorKey = null;
  for (const notify of summaryListeners) notify();
}

/** Display fields derived defensively — dev bypass has no session user. */
export function profileDisplay(user: ProfileUser | null) {
  const name = user?.name?.trim() || "Pioneer";
  const initial = (name[0] ?? "P").toUpperCase();
  const image = user?.image ?? null;
  // Telegram handle is not part of the core better-auth user shape; surface it
  // only if the backend mapped one onto the session user.
  const record = user as unknown as Record<string, unknown> | null;
  const rawHandle = record && typeof record.username === "string" ? record.username : null;
  const handle = rawHandle ? `@${rawHandle.replace(/^@/, "")}` : null;
  return { name, initial, image, handle };
}

export type ProfileDisplay = ReturnType<typeof profileDisplay>;

/** Session + lazy IP/RP summary + sign-in/out actions for the layout chrome. */
export function useProfile() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const user: ProfileUser | null = session?.user ?? null;
  const authenticated = resolveAuthenticated(user);
  const cacheKey = user?.id ?? "dev-bypass";

  // Re-render trigger for when the shared fetch resolves; the rendered value
  // itself is DERIVED from the module cache keyed by the CURRENT user, so a
  // sign-out or user switch can never surface a previous user's numbers.
  const [, setSummaryVersion] = useState(0);
  const summary: ProfileSummary | null =
    authenticated && summaryCache?.key === cacheKey ? summaryCache.value : null;
  /** True once the current user's fetch has failed and not yet succeeded. */
  const summaryError = authenticated && summaryErrorKey === cacheKey;

  useEffect(() => {
    const notify = () => setSummaryVersion((version) => version + 1);
    summaryListeners.add(notify);
    return () => {
      summaryListeners.delete(notify);
    };
  }, []);

  const ensureSummary = useCallback(() => {
    if (!authenticated || summaryCache?.key === cacheKey) return;
    if (!summaryInflight || summaryInflight.key !== cacheKey) {
      summaryInflight = {
        key: cacheKey,
        promise: api.getHomeSummary().then(({ ip, rp, isAdmin }) => ({ ip, rp, isAdmin })),
      };
    }
    void summaryInflight.promise
      .then((value) => {
        summaryCache = { key: cacheKey, value };
        summaryErrorKey = null;
        // Broadcast, not just this instance's own setSummaryVersion — other
        // mounted useProfile() callers (e.g. TopBar reading summary?.isAdmin
        // without itself calling ensureSummary) never attached a .then() to
        // this promise and would otherwise stay stuck on a stale render.
        for (const notify of summaryListeners) notify();
      })
      .catch(() => {
        // Backend RPC unavailable — leave the surfaces on their "—" state, but
        // (a) flag it once via toast (not on every retry) and (b) clear
        // summaryInflight so the next call — a manual reopen OR the header
        // capsule's own retry interval (TopBar.tsx) — isn't blocked and can
        // actually try again instead of leaving the UI stuck forever.
        const firstFailure = summaryErrorKey !== cacheKey;
        summaryInflight = null;
        summaryErrorKey = cacheKey;
        if (firstFailure) toast.error("Couldn't load your IP/RP balance");
        for (const notify of summaryListeners) notify();
      });
  }, [authenticated, cacheKey]);

  const signIn = useCallback((callbackPath: string) => {
    void signInWithTelegram(callbackPath).catch((err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Telegram sign-in failed");
    });
  }, []);

  // lib/auth.ts does not re-export signOut — call the better-auth client directly.
  const signOut = useCallback(async () => {
    try {
      await authClient.signOut();
      // Derived summaries (this instance AND siblings like DrawerProfile) go
      // null on the session change; clearing the cache keeps the next
      // sign-in from reusing a stale key.
      resetSummaryCache();
      toast("Signed out");
    } catch {
      toast.error("Sign out failed");
    }
  }, []);

  return {
    user,
    authenticated,
    sessionPending,
    summary,
    summaryError,
    ensureSummary,
    signIn,
    signOut,
  };
}
